
import { FoundProducts } from '@/model/find-products-response';
import { ProductDisplayMode } from '@/model/product-display-mode';
import { ProductListFilter } from '@/model/product-list-filter';
import { ProductCollection } from '@/service/product';
import { BrowserHistory } from '@/util/history';
import { VueLoaderService } from '@/util/loader';
import { Util } from '@/util/util';
import Vue from 'vue';
import { GtmEnhancedEcommerceType } from '@/model/gtm-enhanced-ecommerce-type';
import { RootVue } from '@/model/root-vue';

const PRODUCTS_PER_PAGE = 12;

export default Vue.extend({
  name: 'ProductCategoryController',
  components: {
    'product-search-wrapper': VueLoaderService.lazyLoadComponent(import('../../components/product-search-wrapper/ProductSearchWrapper.vue')),
    'product-list': VueLoaderService.lazyLoadComponent(import('../../components/v2/product-list/ProductList.vue')),
    paginator: VueLoaderService.lazyLoadComponent(import('../../components/v2/paginator/Paginator.vue')),
    'load-more': VueLoaderService.lazyLoadComponent(import('../../components/load-more/LoadMore.vue')),
    'product-item-display-mode-selector': VueLoaderService.lazyLoadComponent(import('../../components/product-item-display-mode-selector/ProductItemDisplayModeSelector.vue')),
    'active-product-search-label': VueLoaderService.lazyLoadComponent(import('../../components/product-filter/active-product-search-label/ActiveProductSearchLabel.vue')),
    'active-product-filters': VueLoaderService.lazyLoadComponent(import('../../components/product-filter/active-product-filters/ActiveProductFilters.vue')),
    'marketing-banner': VueLoaderService.lazyLoadComponent(import('../../components/marketing-banner/MarketingBanner.vue')),
    'suggested-products': VueLoaderService.lazyLoadComponent(import('../../components/suggested-products/SuggestedProducts.vue')),
    slider: VueLoaderService.lazyLoadComponent(import('../../components/slider/Slider.vue'))
  },
  props: {
    configJsonString: {
      type: String,
      default: '{}'
    },
    gtmEnhancedEcommerceType: {
      type: String,
      default: GtmEnhancedEcommerceType.NONE
    }
  },
  data() {
    return <
      {
        results: FoundProducts | null;
        displayModeOptions: string[];
        activeProductFilter: ProductListFilter;
        productListDisplayMode: string;
        searching: boolean;
        config: { [key: string]: { link: string; columns: string[] } };
      }
    >{
      results: null,
      displayModeOptions: (<any>window)._displayModeOptions || [],
      activeProductFilter: new ProductListFilter(),
      productListDisplayMode: ((<any>window)._displayModeOptions || [ProductDisplayMode.CARD])[0],
      searching: false,
      config: {}
    };
  },
  mounted() {
    VueLoaderService.finishedLoading().then(() => {
      (this.$root as RootVue).vueInitialized = true;
    });

    this.config = JSON.parse(this.configJsonString);
    this.refresh();
  },
  computed: {
    hasResults(): boolean {
      // also hide the message while searching...
      return this.searching || (null != this.results && this.results.totalCount > 0);
    }
  },
  methods: {
    refresh() {
      this.refreshProducts();
      BrowserHistory.getInstance()
        .historyChanged()
        .subscribe(state => {
          // reset the filter upon history change.
          this.activeProductFilter = new ProductListFilter();
          this.refreshProducts();
        });
    },

    pushState() {
      const url = this.activeProductFilter.clone().getUrl();
      window.location.href = url;
      // BrowserHistory.getInstance().pushState({}, "", url);
    },

    calculateTotalProductPagesByCount(totalProducts: number) {
      //return Math.floor(totalProducts / PRODUCTS_PER_PAGE);

      // Ticket: T1908 0419\01, JIRA: https://jira.trimm.nl/browse/TH-19
      // NOTE: Backend already specifies the current page and the total pages as REST call result. Why are we
      //       not using that information?
      if (totalProducts > 0 && PRODUCTS_PER_PAGE > 0) {
        let pages = Math.trunc(totalProducts / PRODUCTS_PER_PAGE);
        return 0 === totalProducts % PRODUCTS_PER_PAGE ? pages : 1 + pages;
      }
      return 0;
    },

    /**
     * Returns the (link, columns(-configuration of a given variant.
     * @param key the variant key/id, e.g. "Truck_Banden".
     */
    getVariantConfig(key: string): { link: string; columns: string[] } {
      const cfg: { link: string; columns: string[] } | undefined = this.config[key];
      return undefined !== cfg ? cfg : { link: '', columns: [] };
    },

    /**
     * Returns the link to the product category page for given variant.
     * @param key the variant key/id, e.g. "Truck_Banden".
     */
    getVariantLink(key: string): string {
      return this.getVariantConfig(key).link;
    },

    /**
     * Returns the columns as defined on the product category page for given variant. Note: isn't used at the
     * moment, but instead we use (currently) static columns as provided by the search results directly.
     * @param key the variant key/id, e.g. "Truck_Banden".
     */
    getVariantColumns(key: string): string[] {
      const columns: string[] = this.getVariantConfig(key).columns;
      if (columns.length > 0) {
        // as defined on the page
        return columns;
      } else if (null !== this.results && undefined !== this.results.variants[key]) {
        // as statically defined (see HeuverEndpoint) - part of the search results
        return this.results.variants[key].columns;
      }
      // fallback columns
      return ['size', 'brand']; // minimal size and brand
    },

    refreshProducts() {
      this.searching = true; // we're searching
      const searchParams = this.activeProductFilter
        .setResultsPerPage(PRODUCTS_PER_PAGE)
        .setBusinessUnit(Util.getProductBusinessUnit())
        .setProductCategory(Util.getProductCategory())
        .getFilter();

      ProductCollection.getInstance()
        .findProducts(searchParams, this.gtmEnhancedEcommerceType)
        .then(results => {
          this.results = results;
        })
        .finally(() => {
          // ready searching (with ot without an error)
          this.searching = false;
        });
    }
  }
});
